var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { useTheme } from '@rad/hooks/useTheme';
import { coreColorVars, resolveThemeColors, applyThemeColors, persistColor, resetThemeColors, } from '../theme/config';
import { withDone } from '@rad/utility/http';
import { getThemeSettings, updateThemeSettings } from '../common/network/district';
// Keys that are CSS color vars (managed by the color pickers)
const colorKeys = new Set(Object.values(coreColorVars));
export const useThemeConfig = () => {
    const [theme, setTheme] = useTheme();
    const [colors, setColors] = React.useState(() => resolveThemeColors(theme));
    const [displaySettings, setDisplaySettings] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    // DB-saved colors keyed by CSS var. Take precedence over localStorage and defaults.
    const savedColors = React.useRef(new Map());
    // Load saved theme settings from DB on mount
    React.useEffect(() => {
        withDone(getThemeSettings())().then((settings) => {
            if (settings && settings.length > 0) {
                const dbValues = new Map(settings.map(s => [s.key, s.value]));
                // Separate color settings from display settings
                const display = {};
                dbValues.forEach((value, key) => {
                    if (!colorKeys.has(key)) {
                        display[key] = value;
                    }
                });
                setDisplaySettings(display);
                savedColors.current = new Map([...dbValues].filter(([key]) => colorKeys.has(key)));
                setColors(prev => {
                    const updated = prev.map(c => {
                        const dbVal = dbValues.get(c.cssVar);
                        if (dbVal)
                            return Object.assign(Object.assign({}, c), { value: dbVal });
                        return c;
                    });
                    applyThemeColors(updated);
                    return updated;
                });
            }
        });
    }, []);
    React.useEffect(() => {
        const resolved = resolveThemeColors(theme).map(c => {
            const saved = savedColors.current.get(c.cssVar);
            return !saved ? c : Object.assign(Object.assign({}, c), { value: saved });
        });
        setColors(resolved);
        applyThemeColors(resolved);
    }, [theme]);
    const updateColor = React.useCallback((name, value) => {
        setColors(prev => {
            const updated = prev.map(c => {
                if (c.name === name) {
                    const next = Object.assign(Object.assign({}, c), { value });
                    persistColor(next);
                    return next;
                }
                return c;
            });
            applyThemeColors(updated);
            return updated;
        });
    }, []);
    const updateDisplaySetting = React.useCallback((key, value) => {
        setDisplaySettings(prev => (Object.assign(Object.assign({}, prev), { [key]: value })));
    }, []);
    const resetColors = React.useCallback(() => {
        const reset = resetThemeColors(theme);
        savedColors.current = new Map();
        setColors(reset);
    }, [theme]);
    const saveTheme = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setSaving(true);
        const colorSettings = colors.map(c => ({
            key: c.cssVar,
            value: c.value,
        }));
        yield updateThemeSettings(colorSettings);
        savedColors.current = new Map(colorSettings.map(s => [s.key, s.value]));
        setSaving(false);
        // Reload page to apply server-side CSS injection with new theme
        window.location.reload();
    }), [colors]);
    return { theme, setTheme, colors, updateColor, resetColors, saveTheme, saving, displaySettings, updateDisplaySetting };
};
export default useThemeConfig;
